import { useMemo } from 'react';
import { Alert, Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Client, Project, TimeEntry } from '@altyor-kronos/shared-types';
import { useAuth } from '../core/auth/useAuth';
import {
  useAllClients,
  useAllDesignations,
  useAllProjects,
  useAllUsers,
  useMyRecentEntries,
  useTeamRecentEntries,
} from '../features/kronos/useKronosData';
import {
  entriesByProject,
  laggingProjects,
  rankedByValue,
  startOfMonth,
  startOfWeekMonday,
} from '../features/kronos/dashboardAggregations';
import { sumDurationSeconds } from '../features/kronos/timeEntries';
import PersonalAggregationPanel from '../features/kronos/dashboard/PersonalAggregationPanel';
import TeamMemberList from '../features/kronos/dashboard/TeamMemberList';
import { PROJECT_COLORS } from '../features/kronos/projectColors';
import { colors } from '../core/theme/theme';

const LAGGING_THRESHOLD = 0.7;

/**
 * Kronos Dashboard (`/kronos/dashboard`) — Phase 7.x evolution.
 *
 * Personal block (everyone):
 *   • Two KPI cards: this-week total, this-month total.
 *   • `PersonalAggregationPanel` for the week: 3 dark KPI cards
 *     (total, top project, top client) + daily stacked bar
 *     (Mon→Sun, per-project) + donut with centre-total + ranked
 *     project list with percentages.
 *
 * Team block (rd_manager only):
 *   • `TeamMemberList` — one row per user with avatar, latest
 *     activity, weekly total and a stacked composition bar.
 *   • "Projects lagging vs hours sold" list (kept from Phase 7).
 *
 * Data sourcing: personal via `useMyRecentEntries`, team via
 * `useTeamRecentEntries` (rd_manager-only), designations via
 * `useAllDesignations` collection group (ditto), plus lightweight
 * `useAllProjects` / `useAllClients` / `useAllUsers` for label +
 * avatar lookups. Every rd_manager-only subscription is gated
 * behind `enabled=false` for plain employees so the query never
 * hits the rule-rejection path.
 */
export default function KronosDashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // Team overview is visible to rd_manager, admin, direction and finance.
  // All four roles have the matching Firestore read permission for
  // `timeEntries` (firestore.rules: isOwner || isRdManager || isAdmin
  // || isDirection || isFinance).
  const canSeeTeamOverview =
    (user?.roles.includes('rd_manager') ?? false) ||
    (user?.roles.includes('admin') ?? false) ||
    (user?.roles.includes('direction') ?? false) ||
    (user?.roles.includes('finance') ?? false);

  // ── Time anchors ──────────────────────────────────────────────
  const now = useMemo(() => new Date(), []);
  const monthStart = useMemo(() => startOfMonth(now), [now]);
  const weekStart = useMemo(() => startOfWeekMonday(now), [now]);

  // Cover both "this month" and "this week" in a single personal
  // subscription. Whichever is earlier wins.
  const personalSince = monthStart < weekStart ? monthStart : weekStart;
  const personal = useMyRecentEntries(user?.uid ?? '', personalSince);

  // Team: pull the WEEK (not month) since the team widget visualises
  // compliance at the week level.
  const team = useTeamRecentEntries(weekStart, canSeeTeamOverview);
  const designations = useAllDesignations(canSeeTeamOverview);
  const { projects: projectRows } = useAllProjects();
  const { clients: clientsById } = useAllClients();
  const { users: userRows } = useAllUsers(canSeeTeamOverview);

  // ── Lookup maps ────────────────────────────────────────────────
  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projectRows) m.set(p.id, p.data);
    return m;
  }, [projectRows]);

  // ── Personal split ─────────────────────────────────────────────
  const personalEntries = personal.entries.map((e) => e.data);
  const thisMonthPersonal = useMemo<TimeEntry[]>(
    () => personalEntries.filter((e) => e.startTime.toDate() >= monthStart),
    [personalEntries, monthStart],
  );
  const thisWeekPersonal = useMemo<TimeEntry[]>(
    () => personalEntries.filter((e) => e.startTime.toDate() >= weekStart),
    [personalEntries, weekStart],
  );

  // ── Team computed ──────────────────────────────────────────────
  const teamEntries = team.entries.map((e) => e.data);
  const lagging = useMemo(
    () => laggingProjects(designations.designations, teamEntries, LAGGING_THRESHOLD),
    [designations.designations, teamEntries],
  );

  // Shared colour map: reuse the team-week project ranking so every
  // stacked bar in the list uses the same hue for the same project.
  // Matches (but is independent from) the personal panel's palette —
  // the viewer's own segments may differ because their personal
  // ranking differs from the team's, and that's acceptable.
  const teamProjectColorById = useMemo(() => {
    const ranked = rankedByValue(entriesByProject(teamEntries));
    const m = new Map<string, string>();
    ranked.forEach((row, i) => {
      m.set(row.key, PROJECT_COLORS[i % PROJECT_COLORS.length]);
    });
    return m;
  }, [teamEntries]);

  // Filter to users who have at least one time-logging role.
  // Direction and admin don't log time; showing them in the team list
  // creates empty rows and skews the "no activity" count.
  const teamUsers = useMemo(
    () => userRows.filter((u) => u.data.roles.some((r) => r !== 'direction' && r !== 'admin')),
    [userRows],
  );

  // ── Rendering ──────────────────────────────────────────────────
  const personalError = personal.error;
  const teamError = team.error ?? designations.error;

  return (
    <Box data-testid="kronos-dashboard">
      {/* Hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 4 },
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          component="img"
          src="/images/dashboard-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('kronos.dashboard.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('kronos.dashboard.subtitle')}
          </Typography>
        </Stack>
      </Box>

      {personalError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="dashboard-personal-error">
          {personalError.message}
        </Alert>
      )}

      {/* ── Week / month summary KPI cards ──────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderColor: colors.lightGray, borderRadius: 0 }}
            data-testid="kpi-personal-week"
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('kronos.dashboard.kpiWeek')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
                sumDurationSeconds(thisWeekPersonal) / 3600,
              )}{' '}
              h
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderColor: colors.lightGray, borderRadius: 0 }}
            data-testid="kpi-personal-month"
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('kronos.dashboard.kpiMonth')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
                sumDurationSeconds(thisMonthPersonal) / 3600,
              )}{' '}
              h
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Personal aggregation panel (4 KPI cards + charts) ──── */}
      <PersonalAggregationPanel
        weekEntries={thisWeekPersonal}
        weekStart={weekStart}
        projectsById={projectsById}
        clientsById={clientsById as ReadonlyMap<string, Client>}
        monthSeconds={sumDurationSeconds(thisMonthPersonal)}
      />

      {/* ── Team overview (rd_manager only) ─────────────────────── */}
      {canSeeTeamOverview && (
        <Box sx={{ mt: 5 }} data-testid="team-overview">
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t('kronos.dashboard.teamTitle')}
          </Typography>

          {teamError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="dashboard-team-error">
              {teamError.message}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TeamMemberList
                weekEntries={teamEntries}
                users={teamUsers}
                projectsById={projectsById}
                projectColorById={teamProjectColorById}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderColor: colors.lightGray,
                  borderRadius: 0,
                  height: '100%',
                }}
                data-testid="lagging-panel"
              >
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('kronos.dashboard.laggingTitle')}
                </Typography>
                {lagging.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('kronos.dashboard.laggingEmpty')}
                  </Typography>
                ) : (
                  <Stack spacing={1.5} data-testid="lagging-list">
                    {lagging.map((l) => (
                      <LaggingRow
                        key={l.projectId}
                        label={projectLabel(projectsById, l.projectId)}
                        ratio={l.ratio}
                        hoursSpent={l.hoursSpent}
                        hoursSold={l.hoursSold}
                        locale={i18n.language}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}

// ─── Small presentational components ───────────────────────────────

function LaggingRow({
  label,
  ratio,
  hoursSpent,
  hoursSold,
  locale,
}: {
  label: string;
  ratio: number;
  hoursSpent: number;
  hoursSold: number;
  locale: string;
}) {
  const barPct = Math.min(ratio, 1.2) * 100;
  const color = ratio >= 0.9 ? colors.red : colors.yellow;
  return (
    <Box data-testid="lagging-row">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {round1(hoursSpent).toLocaleString(locale)}h / {round1(hoursSold).toLocaleString(locale)}h
          {' · '}
          {Math.round(ratio * 100)}%
        </Typography>
      </Box>
      <Box sx={{ height: 6, bgcolor: colors.lightGray }}>
        <Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: color }} />
      </Box>
    </Box>
  );
}

// ─── Pure helpers ──────────────────────────────────────────────────

function projectLabel(projectsById: ReadonlyMap<string, Project>, id: string): string {
  const p = projectsById.get(id);
  return p ? `${p.code} — ${p.name}` : id;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

